import { useEffect, useState } from 'react'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useTaskDrawer } from '@/contexts/TaskDrawerContext'
import { toCreatePayload, valuesFromPrefill, type TaskFormValues } from '@/lib/task-form'
import type { TaskView } from '@/lib/tasks-board'
import { taskService } from '@/services/task-service'
import { taskBoardService, type Carteira } from '@/services/task-board-service'
import { apiErrorMessage, teamService, type TeamMember } from '@/services/team-service'
import * as toast from '@/lib/toast'
import { TaskDetailsPanel } from './TaskDetailsPanel'
import { TaskForm } from './TaskForm'

/**
 * Gaveta lateral única de tarefa: cria, mostra e edita. Aberta de qualquer tela
 * (publicação, processo, cliente, agenda, listas) pelo `useTaskDrawer()`.
 */
export function TaskDrawer() {
  const { isAuthenticated, user } = useAuth()
  const drawer = useTaskDrawer()
  const current = drawer.current
  const open = isAuthenticated && current !== null
  const [members, setMembers] = useState<TeamMember[]>([])
  const [carteiras, setCarteiras] = useState<Carteira[]>([])
  const [task, setTask] = useState<TaskView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    teamService.listMembers().then((list) => setMembers(list.filter((m) => m.isActive))).catch(() => setMembers([]))
    taskBoardService.listCarteiras().then(setCarteiras).catch(() => setCarteiras([]))
  }, [open])

  useEffect(() => {
    setTask(null)
    setError(null)
    if (typeof current !== 'number') return
    let active = true
    taskBoardService.get(current)
      .then((loaded) => { if (active) setTask(loaded) })
      .catch((err) => { if (active) setError(apiErrorMessage(err, 'Tarefa não encontrada')) })
    return () => { active = false }
  }, [current])

  const create = async (values: TaskFormValues) => {
    setSaving(true)
    try {
      const response = await taskService.create(toCreatePayload(values, drawer.prefill))
      const created = response.data as { task: TaskView; aviso_prazo?: string | null; agenda_solicitada?: boolean }
      toast.success('Tarefa criada', created.agenda_solicitada ? 'O compromisso aparece na agenda em instantes.' : undefined)
      if (created.aviso_prazo) toast.warning(created.aviso_prazo)
      drawer.notifyChanged(created.task.id)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível criar a tarefa'))
    } finally {
      setSaving(false)
    }
  }

  const title = drawer.current === 'nova' ? 'Nova tarefa' : task?.title ?? 'Tarefa'
  const description = drawer.current === 'nova'
    ? drawer.prefill?.origemLabel ?? 'Comece pelo verbo: "Protocolar…", "Ligar…", "Revisar…"'
    : task ? `${task.etapa_label} · ${task.assigned_user_name}${task.case_number ? ` · ${task.case_number}` : ''}` : ''

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) drawer.close() }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <div className="px-4 pb-6">
          {drawer.current === 'nova' && user && (
            <TaskForm
              key={JSON.stringify(drawer.prefill ?? {})}
              initial={valuesFromPrefill(drawer.prefill, user.id)}
              members={members}
              carteiras={carteiras}
              mode="create"
              saving={saving}
              onSubmit={create}
              onCancel={drawer.close}
            />
          )}
          {typeof drawer.current === 'number' && error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {typeof drawer.current === 'number' && !task && !error && (
            <div className="flex flex-col gap-3"><Skeleton className="h-8" /><Skeleton className="h-40" /></div>
          )}
          {task && (
            <TaskDetailsPanel
              task={task}
              members={members}
              carteiras={carteiras}
              onChanged={(updated) => {
                if (updated) setTask(updated)
                drawer.notifyChanged()
              }}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
